/**
 * conductor-pasajero-toggle.js — Toggle "Conductor / Pasajero" accesible.
 *
 * Convierte el selector de la Home en un patrón de pestañas (tabs) correcto:
 *   - Los <a> sin href se transforman en <button role="tab" ...> reales.
 *   - El contenedor .wp-block-buttons se marca como role="tablist".
 *   - Cada slider (.slider-conductor / .slider-pasajero) se marca como
 *     role="tabpanel" y se enlaza con aria-labelledby / aria-controls.
 *   - Se gestiona aria-selected y tabindex (roving tabindex).
 *   - Se añade navegación por teclado (flechas / Home / End).
 *
 * El estado visual (clases .is-active / .activa) puede ser pilotado tanto por
 * este módulo como por el <script> inline de la BBDD; por eso un
 * MutationObserver mantiene ARIA sincronizado con la clase activa real, sea
 * cual sea la vía por la que cambió.
 *
 * @package waiis
 */

const TABS = [
	{ trigger: '.toggle-conductor', panel: '.slider-conductor' },
	{ trigger: '.toggle-pasajero',  panel: '.slider-pasajero'  },
];

/**
 * Convierte un <a> en un <button> equivalente, conservando clases y atributos.
 *
 * @param {HTMLAnchorElement} anchor
 * @return {HTMLButtonElement}
 */
function convertAnchorToButton( anchor ) {
	const button = document.createElement( 'button' );

	if ( anchor.className ) {
		button.className = anchor.className;
	}

	button.innerHTML = anchor.innerHTML;

	Array.from( anchor.attributes ).forEach( ( attr ) => {
		if ( 'href' === attr.name || 'class' === attr.name || 'id' === attr.name ) {
			return;
		}
		button.setAttribute( attr.name, attr.value );
	} );

	anchor.replaceWith( button );

	return button;
}

/**
 * Inicializa el toggle de Conductor / Pasajero.
 */
export function initConductorPasajeroToggle() {
	const wrapper = document.querySelector( '.toggle-wrapper' );
	if ( ! wrapper ) return;

	const host = wrapper.querySelector( '.wp-block-buttons' );

	const entries = [];

	TABS.forEach( ( def ) => {
		const triggerWrap = wrapper.querySelector( def.trigger );
		const panel       = document.querySelector( def.panel );
		if ( ! triggerWrap || ! panel ) return;

		// El "botón" puede ser un <a> (BBDD) o un <button> ya corregido.
		let btn = triggerWrap.querySelector( '.wp-block-button__link, button' );
		if ( ! btn ) return;

		if ( btn.tagName.toLowerCase() === 'a' ) {
			btn = convertAnchorToButton( btn );
		}

		btn.setAttribute( 'type', 'button' );

		const uid = `waiis-tab-${ def.trigger.replace( '.', '' ) }`;
		btn.setAttribute( 'id', uid );
		btn.setAttribute( 'role', 'tab' );
		btn.setAttribute( 'aria-controls', `panel-${ uid }` );

		panel.setAttribute( 'id', `panel-${ uid }` );
		panel.setAttribute( 'role', 'tabpanel' );
		panel.setAttribute( 'aria-labelledby', uid );
		panel.setAttribute( 'tabindex', '0' );

		entries.push( { btn, triggerWrap, panel } );
	} );

	if ( entries.length < 2 ) return;

	if ( host ) {
		host.setAttribute( 'role', 'tablist' );
		host.setAttribute( 'aria-label', 'Conductor o pasajero' );
	}

	const isActive = ( entry ) =>
		entry.triggerWrap.classList.contains( 'is-active' ) ||
		entry.panel.classList.contains( 'activa' );

	// ── Sincroniza ARIA con el estado visual actual (idempotente) ─────────────
	function sync() {
		entries.forEach( ( entry ) => {
			const active = isActive( entry );
			entry.btn.setAttribute( 'aria-selected', active ? 'true' : 'false' );
			entry.btn.setAttribute( 'tabindex', active ? '0' : '-1' );
		} );
	}

	// ── Activa la pestaña indicada (clases visuales + ARIA + foco) ──────────
	function activate( index, focus ) {
		entries.forEach( ( entry, i ) => {
			const active = i === index;
			entry.triggerWrap.classList.toggle( 'is-active', active );
			entry.panel.classList.toggle( 'activa', active );
		} );
		sync();
		if ( focus ) entries[ index ].btn.focus();
	}

	// ── Interacción: clic y teclado ──────────────────────────────────────────
	entries.forEach( ( entry, i ) => {
		entry.btn.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			activate( i, false );
		} );

		entry.btn.addEventListener( 'keydown', ( e ) => {
			const count = entries.length;
			let next = null;

			if ( e.key === 'ArrowRight' ) next = ( i + 1 ) % count;
			else if ( e.key === 'ArrowLeft' ) next = ( i - 1 + count ) % count;
			else if ( e.key === 'Home' ) next = 0;
			else if ( e.key === 'End' ) next = count - 1;

			if ( next !== null ) {
				e.preventDefault();
				activate( next, true );
			}
		} );
	} );

	// ── Observa cambios de clase (por el <script> inline de BBDD u otra vía) ──
	const observed = [];
	entries.forEach( ( entry ) => {
		observed.push( entry.triggerWrap, entry.panel );
	} );

	const observer = new MutationObserver( sync );
	observed.forEach( ( el ) => {
		observer.observe( el, { attributes: true, attributeFilter: [ 'class' ] } );
	} );

	// Estado inicial.
	sync();
}